// File chips (attachments.md section 8): in the composer while each file uploads, with its progress,
// Retry and Remove; and on a sent message, where a click opens AlphaLab's Drive page (in the panel
// when the screen is wide). Names truncate; the size and percent stay visible at any width.
import { AttachmentPrimitive, useAui, useAuiState, type Attachment } from "@assistant-ui/react";
import { File, FileArchive, FileImage, FileSpreadsheet, FileText, Loader2, RotateCw, X } from "lucide-react";
import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import { formatBytes, type AttachmentRef, type DriveUploads, type Upload } from "../attachments.js";
import { driveUrl } from "../links.js";
import { cn } from "../lib/utils.js";
import { OpenLinkContext } from "./Thread.js";

/** The composer's uploads, where attachments are offered. */
export const UploadsContext = createContext<DriveUploads | undefined>(undefined);

const noSubscribe = () => () => undefined;

/** One upload's state, as its chip shows it; undefined for a file not uploading in this page. */
export function useUpload(id: string): Upload | undefined {
  const uploads = useContext(UploadsContext);
  return useSyncExternalStore(uploads?.subscribe ?? noSubscribe, () => uploads?.get(id));
}

function FileIcon({ name, contentType }: { name: string; contentType?: string }) {
  const type = contentType ?? "";
  const ext = name.toLowerCase().split(".").pop() ?? "";
  const Icon = type.startsWith("image/")
    ? FileImage
    : ["csv", "tsv", "xls", "xlsx", "ods"].includes(ext) || type.includes("spreadsheet") || type === "text/csv"
      ? FileSpreadsheet
      : ["zip", "gz", "tgz", "tar", "7z", "rar"].includes(ext)
        ? FileArchive
        : type.startsWith("text/") || ["pdf", "md", "txt", "json", "xml", "html", "htm", "doc", "docx"].includes(ext)
          ? FileText
          : File;
  return <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />;
}

function percentOf(u: Upload): number {
  return u.sizeBytes ? Math.min(100, Math.floor((u.sent / u.sizeBytes) * 100)) : 0;
}

/** What a chip says of an upload: waiting, a spinner, its percent and bytes, or its size. */
function UploadStatus({ u }: { u: Upload }) {
  if (u.phase === "waiting") return <>waiting</>;
  if (u.phase === "requesting") return <Loader2 className="size-3.5 animate-spin" aria-label="Starting" />;
  if (u.phase === "uploading") {
    // One inline run, so its spaces survive the flex row; on a phone only the percent and the size.
    return (
      <span>
        {percentOf(u)}%<span className="max-sm:hidden"> · {formatBytes(u.sent)} of</span> {formatBytes(u.sizeBytes)}
      </span>
    );
  }
  return <>{formatBytes(u.sizeBytes)}</>;
}

function Chip({ failed, children, className }: { failed?: boolean; children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "relative flex min-w-0 max-w-full flex-col overflow-hidden rounded-md border text-[13px] sm:max-w-72",
        failed ? "border-error/50 bg-error-bg text-error" : "border-border bg-background text-foreground",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A file in the composer: uploading, uploaded, or failed with its reason, Retry and Remove (section 5, States). */
export function ComposerAttachment({ attachment }: { attachment: Attachment }) {
  const aui = useAui();
  const uploads = useContext(UploadsContext);
  const u = useUpload(attachment.id);
  const failed = u?.phase === "failed";
  const retry = () => {
    const file = uploads?.retryFile(attachment.id);
    // The same id and Drive path: the chip stays where it is, and the API first checks whether it landed.
    if (file) void aui.composer.addAttachment(file).catch(() => undefined);
  };
  return (
    <Chip failed={failed}>
      <div className="flex min-w-0 items-center gap-1.5 py-1 pr-1 pl-2">
        <FileIcon name={attachment.name} contentType={attachment.contentType} />
        <span className="min-w-0 flex-1 truncate" title={attachment.name}>
          {attachment.name}
        </span>
        <span className="flex shrink-0 items-center text-xs tabular-nums text-muted-foreground">{u && <UploadStatus u={u} />}</span>
        {failed && (
          <button type="button" onClick={retry} aria-label={`Retry ${attachment.name}`} title="Retry" className="shrink-0 rounded p-1 hover:bg-accent">
            <RotateCw className="size-3.5" />
          </button>
        )}
        <AttachmentPrimitive.Remove asChild>
          <button type="button" aria-label={`Remove ${attachment.name}`} title="Remove" className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
            <X className="size-3.5" />
          </button>
        </AttachmentPrimitive.Remove>
      </div>
      {failed && u.reason && <p className="px-2 pb-1.5 text-xs">{u.reason}</p>}
      {u?.phase === "uploading" && <span className="absolute bottom-0 left-0 h-0.5 bg-primary" style={{ width: `${percentOf(u)}%` }} />}
    </Chip>
  );
}

/**
 * A file on a sent message: its name, size and type, opening AlphaLab's Drive page. While the
 * composer's submission still waits for its upload, the chip shows the upload instead.
 */
export function SentAttachment({ attachment }: { attachment: Attachment }) {
  const refs = useAuiState((s) => s.message.metadata.custom.attachments) as AttachmentRef[] | undefined;
  const open = useContext(OpenLinkContext);
  const u = useUpload(attachment.id);
  const ref = refs?.find((r) => r.path === attachment.id);
  const url = driveUrl();
  const content = (
    <div className="flex min-w-0 items-center gap-1.5 px-2 py-1">
      <FileIcon name={attachment.name} contentType={attachment.contentType} />
      <span className="min-w-0 flex-1 truncate">{attachment.name}</span>
      <span className="flex shrink-0 items-center text-xs tabular-nums text-muted-foreground">
        {u ? <UploadStatus u={u} /> : ref ? formatBytes(ref.sizeBytes) : null}
      </span>
    </div>
  );
  if (!ref || !url) return <Chip>{content}</Chip>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={`${ref.name} · in your AlphaLab Drive`}
      className="min-w-0 max-w-full hover:opacity-80 sm:max-w-72"
      onClick={(e) => {
        if (open(url, "Drive")) e.preventDefault();
      }}
    >
      <Chip className="sm:max-w-none">{content}</Chip>
    </a>
  );
}
